import React, { useState, useMemo } from 'react';
import { 
    Box, 
    Typography, 
    Grid, 
    Paper, 
    Chip, 
    Divider,
    Card,
    CardContent,
    useTheme,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Snackbar,
    Alert,
    CircularProgress
} from '@mui/material';
import { SandboxConsentDetails } from '../../../services/sandbox-consents-service';
import { vaultService } from '../../../services/vault-service';
import DescriptionIcon from '@mui/icons-material/Description';
import WarningIcon from '@mui/icons-material/Warning';
import SecurityIcon from '@mui/icons-material/Security';
import StorageIcon from '@mui/icons-material/Storage';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import NumbersIcon from '@mui/icons-material/Numbers';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockIcon from '@mui/icons-material/Lock';

interface DataElementsTabProps {
    consentData: SandboxConsentDetails;
}

const DataElementsTab: React.FC<DataElementsTabProps> = ({ consentData }) => {
    const theme = useTheme();
    const { dataElements } = consentData;
    
    
    // State for PII value viewing
    const [viewValueDialog, setViewValueDialog] = useState(false);
    const [selectedElement, setSelectedElement] = useState<any>(null);
    const [actualValue, setActualValue] = useState<string>('');
    const [maskedToken, setMaskedToken] = useState<string>('');
    const [loadingValue, setLoadingValue] = useState(false);
    const [valueError, setValueError] = useState<string>('');
    const [copySuccess, setCopySuccess] = useState(false);

    // Get data type icon
    const getDataTypeIcon = (types: string[]) => {
        const typeString = types.join(' ').toLowerCase();
        if (typeString.includes('email')) return <EmailIcon sx={{ fontSize: 16 }} />;
        if (typeString.includes('phone')) return <PhoneIcon sx={{ fontSize: 16 }} />;
        if (typeString.includes('date')) return <CalendarTodayIcon sx={{ fontSize: 16 }} />;
        if (typeString.includes('number') || typeString.includes('numeric')) return <NumbersIcon sx={{ fontSize: 16 }} />;
        if (typeString.includes('boolean') || typeString.includes('checkbox')) return <ToggleOnIcon sx={{ fontSize: 16 }} />;
        return <TextFieldsIcon sx={{ fontSize: 16 }} />;
    };

    // Get data type color
    const getDataTypeColor = (types: string[]): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'default' => {
        const typeString = types.join(' ').toLowerCase();
        if (typeString.includes('email')) return 'primary';
        if (typeString.includes('phone')) return 'success';
        if (typeString.includes('date')) return 'secondary';
        if (typeString.includes('number') || typeString.includes('numeric')) return 'warning';
        if (typeString.includes('boolean') || typeString.includes('checkbox')) return 'info';
        return 'default';
    };

    // Format value for display
    const formatValue = (value: any, types: string[]): string => {
        if (value === null || value === undefined || value === '') {
            return 'Not provided';
        }
        
        const typeString = types.join(' ').toLowerCase();
        
        if (typeString.includes('date')) {
            try {
                return new Date(value).toLocaleDateString();
            } catch {
                return String(value);
            }
        }
        
        if (typeString.includes('boolean')) {
            return value ? 'Yes' : 'No';
        }
        
        return String(value);
    };

    // Check if value is provided
    const isValueProvided = (value: any): boolean => {
        return value !== null && value !== undefined && value !== '';
    };


    // Calculate summary statistics
    const summaryStats = useMemo(() => {
        const total = dataElements.length;
        const required = dataElements.filter(e => e.isRequired).length;
        const personalData = dataElements.filter(e => e.isPersonalData).length;
        const provided = dataElements.filter(e => isValueProvided(e.value)).length;
        
        return { total, required, personalData, provided };
    }, [dataElements]);


    // Handle viewing PII values (both actual and masked)
    const handleViewPIIValues = async (element: any) => {
        setSelectedElement(element);
        setViewValueDialog(true);
        setLoadingValue(true);
        setValueError('');
        setActualValue('');
        setMaskedToken('');

        try {
            console.log('Element data:', element);
            console.log('VaultId:', element.vaultId);
            console.log('Storage mode:', element.storageMode);
            console.log('Is personal data:', element.isPersonalData);
            
            if (element.storageMode === 'VAULT' && element.isPersonalData && element.vaultId) {
                // Fetch both actual value and masked token in parallel
                const [actualResult, maskedResult] = await Promise.allSettled([
                    vaultService.getActualValue(element.vaultId),
                    vaultService.getMaskedToken(element.vaultId)
                ]);

                // Handle actual value result
                if (actualResult.status === 'fulfilled') {
                    setActualValue(actualResult.value.originalValue);
                } else {
                    console.error('Error fetching actual value:', actualResult.reason);
                    // Check if it's a 403 permission error
                    if (actualResult.reason?.response?.status === 403) {
                        setActualValue('Insufficient permissions to view actual value');
                    } else {
                        setActualValue('Failed to retrieve actual value');
                    }
                }

                // Handle masked token result
                if (maskedResult.status === 'fulfilled') {
                    setMaskedToken(maskedResult.value.partiallyMaskedToken);
                } else {
                    console.error('Error fetching masked token:', maskedResult.reason);
                    // Check if it's a 403 permission error
                    if (maskedResult.reason?.response?.status === 403) {
                        setMaskedToken('Insufficient permissions to view masked token');
                    } else {
                        setMaskedToken('Failed to retrieve masked token');
                    }
                }
            } else {
                setValueError('This data element is not stored in the vault or is not personal data.');
            }
        } catch (error) {
            console.error('Error fetching PII values:', error);
            setValueError('Failed to retrieve the PII values. Please try again.');
        } finally {
            setLoadingValue(false);
        }
    };

    // Handle copying value to clipboard
    const handleCopyToClipboard = async (value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopySuccess(true);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
        }
    };

    // Close the dialog
    const handleCloseDialog = () => {
        setViewValueDialog(false);
        setSelectedElement(null);
        setActualValue('');
        setMaskedToken('');
        setValueError('');
    };

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <DescriptionIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h5" component="h2" gutterBottom>
                            Data Elements
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Personal data collected during consent submission
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Summary Statistics Cards */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: 'auto' }}>
                        <CardContent sx={{ textAlign: 'center', py: 1.5, px: 2 }}>
                            <DescriptionIcon sx={{ fontSize: 24, color: 'primary.main', mb: 0.5 }} />
                            <Typography variant="h5" component="div" color="primary">
                                {summaryStats.total}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Total Elements
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: 'auto' }}>
                        <CardContent sx={{ textAlign: 'center', py: 1.5, px: 2 }}>
                            <WarningIcon sx={{ fontSize: 24, color: 'warning.main', mb: 0.5 }} />
                            <Typography variant="h5" component="div" color="warning.main">
                                {summaryStats.required}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Required Elements
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: 'auto' }}>
                        <CardContent sx={{ textAlign: 'center', py: 1.5, px: 2 }}>
                            <SecurityIcon sx={{ fontSize: 24, color: 'info.main', mb: 0.5 }} />
                            <Typography variant="h5" component="div" color="info.main">
                                {summaryStats.personalData}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Personal Data
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: 'auto' }}>
                        <CardContent sx={{ textAlign: 'center', py: 1.5, px: 2 }}>
                            <CheckCircleIcon sx={{ fontSize: 24, color: 'success.main', mb: 0.5 }} />
                            <Typography variant="h5" component="div" color="success.main">
                                {summaryStats.provided}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Values Provided
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>


            {/* Data Elements List */}
            <Box>
                <Typography variant="h6" gutterBottom>
                    Data Elements ({dataElements.length})
                </Typography>
                
                {dataElements.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No data elements found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            No data elements were collected for this consent
                        </Typography>
                    </Paper>
                ) : (
                    <Grid container spacing={2}>
                        {dataElements.map((element) => (
                            <Grid item xs={12} key={element.id}>
                                <Paper sx={{ p: 3 }}>
                                    <Grid container spacing={3} alignItems="flex-start">
                                        {/* Left Section - Element Info */}
                                        <Grid item xs={12} md={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                {getDataTypeIcon(element.type)}
                                                <Typography variant="h6" component="h3">
                                                    {element.name}
                                                </Typography>
                                            </Box>
                                            
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    Key
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                                                    {element.key}
                                                </Typography>
                                            </Box>
                                            
                                            {element.description && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        Description
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {element.description}
                                                    </Typography>
                                                </Box>
                                            )}
                                            
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <Chip
                                                    icon={getDataTypeIcon(element.type)}
                                                    label={element.type.join(', ')}
                                                    color={getDataTypeColor(element.type)}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                                <Chip
                                                    icon={element.isRequired ? <WarningIcon /> : <CheckCircleIcon />}
                                                    label={element.isRequired ? 'Required' : 'Optional'}
                                                    color={element.isRequired ? 'error' : 'default'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Box>
                                        </Grid>
                                        
                                        {/* Right Section - Value & Metadata */}
                                        <Grid item xs={12} md={6}>
                                            <Box sx={{ mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Provided Value
                                                    </Typography>
                                                    {element.isPersonalData && element.storageMode === 'VAULT' && (
                                                        <Tooltip title="Click to view actual value and masked token">
                                                            <Typography variant="caption" color="primary" sx={{ fontStyle: 'italic' }}>
                                                                (Click to view details)
                                                            </Typography>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                                <Typography 
                                                    variant="body1" 
                                                    sx={{ 
                                                        fontFamily: isValueProvided(element.value) ? 'inherit' : 'monospace',
                                                        color: isValueProvided(element.value) ? 'text.primary' : 'text.secondary',
                                                        fontStyle: isValueProvided(element.value) ? 'normal' : 'italic',
                                                        cursor: element.isPersonalData && element.storageMode === 'VAULT' ? 'pointer' : 'default',
                                                        textDecoration: element.isPersonalData && element.storageMode === 'VAULT' ? 'underline' : 'none',
                                                        '&:hover': element.isPersonalData && element.storageMode === 'VAULT' ? {
                                                            color: 'primary.main',
                                                            textDecoration: 'underline'
                                                        } : {}
                                                    }}
                                                    onClick={() => {
                                                        if (element.isPersonalData && element.storageMode === 'VAULT') {
                                                            handleViewPIIValues(element);
                                                        }
                                                    }}
                                                >
                                                    {formatValue(element.value, element.type)}
                                                </Typography>
                                            </Box>
                                            
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                                <Chip
                                                    icon={element.isPersonalData ? <SecurityIcon /> : <CheckCircleIcon />}
                                                    label={element.isPersonalData ? 'Personal Data' : 'Non-Personal Data'}
                                                    color={element.isPersonalData ? 'info' : 'success'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                                <Chip
                                                    icon={isValueProvided(element.value) ? <CheckCircleIcon /> : <CancelIcon />}
                                                    label={isValueProvided(element.value) ? 'Provided' : 'Missing'}
                                                    color={isValueProvided(element.value) ? 'success' : 'error'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Box>
                                            
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    Storage Mode
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <StorageIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                    <Typography variant="body2">
                                                        {element.storageMode || 'Standard'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>

            {/* View Actual Value Dialog */}
            <Dialog 
                open={viewValueDialog} 
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LockIcon color="primary" />
                        <Typography variant="h6">
                            PII Data Details
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedElement && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Data Element: {selectedElement.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Key: {selectedElement.key}
                            </Typography>
                            
                            <Divider sx={{ my: 2 }} />
                            
                            {loadingValue ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" color="text.secondary">
                                        Retrieving PII data...
                                    </Typography>
                                </Box>
                            ) : valueError ? (
                                <Box sx={{ py: 2 }}>
                                    <Alert severity="error">
                                        {valueError}
                                    </Alert>
                                </Box>
                            ) : (actualValue || maskedToken) ? (
                                <Box>
                                    {maskedToken && (
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                Partially Masked Token:
                                            </Typography>
                                            <Box sx={{ 
                                                p: 2, 
                                                bgcolor: 'background.paper', 
                                                borderRadius: 1, 
                                                border: 1, 
                                                borderColor: maskedToken.includes('permissions') ? 'warning.main' : 'divider',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <Typography 
                                                    variant="body1" 
                                                    sx={{ 
                                                        fontFamily: maskedToken.includes('permissions') ? 'inherit' : 'monospace',
                                                        flexGrow: 1,
                                                        wordBreak: 'break-all',
                                                        color: maskedToken.includes('permissions') ? 'warning.dark' : 'text.primary'
                                                    }}
                                                >
                                                    {maskedToken}
                                                </Typography>
                                                {!maskedToken.includes('permissions') && (
                                                    <Tooltip title="Copy to clipboard">
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => handleCopyToClipboard(maskedToken)}
                                                            color="primary"
                                                        >
                                                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                    
                                    {actualValue && (
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                Actual Value:
                                            </Typography>
                                            <Box sx={{ 
                                                p: 2, 
                                                borderRadius: 1, 
                                                border: 1, 
                                                borderColor: actualValue.includes('permissions') ? 'warning.main' : 'error.main',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <Typography 
                                                    variant="body1" 
                                                    sx={{ 
                                                        fontFamily: actualValue.includes('permissions') ? 'inherit' : 'monospace',
                                                        flexGrow: 1,
                                                        wordBreak: 'break-all',
                                                        color: actualValue.includes('permissions') ? 'warning.dark' : 'error.dark'
                                                    }}
                                                >
                                                    {actualValue}
                                                </Typography>
                                                {!actualValue.includes('permissions') && (
                                                    <Tooltip title="Copy to clipboard">
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => handleCopyToClipboard(actualValue)}
                                                            sx={{ 
                                                                color: 'error.dark',
                                                                '&:hover': {
                                                                    bgcolor: 'error.main',
                                                                    color: 'error.contrastText'
                                                                }
                                                            }}
                                                        >
                                                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            ) : null}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Copy Success Snackbar */}
            <Snackbar
                open={copySuccess}
                autoHideDuration={3000}
                onClose={() => setCopySuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={() => setCopySuccess(false)} 
                    severity="success" 
                    sx={{ width: '100%' }}
                >
                    Value copied to clipboard!
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default DataElementsTab;
